import {
  corePhasesManager,
  modes,
  playersManager,
  requestManager,
  type TrainingDataFromResultsRequest,
  trainingManager,
  gameStats,
  minigameConfig,
  type PlayerInfo,
  type TournamentDataFromResultsRequest,
  gsap
} from '@powerplay/core-minigames'
import { tutorialObjectives } from './modes/tutorial/TutorialObjectives'
import {
  TutorialObjectiveIds,
  type OpponentResult,
  type SaveResultsDataToSend
} from './types'
import { gameConfig } from './config'
import { player } from './entities/athlete/player'
import { disciplinePhasesManager } from './phases/DisciplinePhasesManager'
import {
  blurState,
  trainingResultsState
} from '@/stores'
import { stateManager } from './StateManager'

/**
 * Trieda pre koniec discipliny
 */
export class EndManager {

  /** ci uz bol result poslany alebo nie */
  private resultSent = false

  /** UUID vitaza */
  private winnerUuid = ''

  /** Cas vitaza */
  private winnerTime = minigameConfig.dnfValue

  /** ci sa skoncilo predcasne */
  public prematureEnded = false

  /** Ci bol hrac diskvalifikovany */
  public wasDsq = false

  /** Svetovy rekord */
  public worldRecord = 0

  /** Kvalita startu */
  public startQuality = 0

  /** Kvalita lunge-u */
  public lungeQuality = 0

  /** Ci bol perfect alebo excellent start */
  public perfectOrExcellentStart = false

  /** ci bol perfect lunge */
  public perfectLunge = false

  /**
   * Nastavenie svetoveho rekordu
   */
  public setWorldRecord(): void {

    const personalBest = playersManager.getPlayer().personalBest
    this.worldRecord = gameConfig.worldRecord
    if (personalBest > 0 && personalBest < this.worldRecord) this.worldRecord = personalBest

  }

  /**
   * Zistenie, ci je atlet v cieli vitazom podla frame-u
   * @param uuid - Uuid hraca
   * @returns True, ak ano
   */
  public isWinnerInFinish(uuid: string): boolean {

    return this.winnerUuid === uuid

  }

  /**
   * Zistenie, ci uz je nejaky atlet v cieli
   * @returns True, ak ano
   */
  public isSomeoneInFinish(): boolean {

    return this.winnerTime !== minigameConfig.dnfValue

  }

  /**
   * Nastavenie potencialneho vitaza v cieli
   * @param uuid - uuid potencialneho vitaza
   * @param time - cas potencialneho vitaza
   */
  public setPotentialWinnerInFinish(uuid: string, time: number): void {

    if (time < this.winnerTime) {

      this.winnerTime = time
      this.winnerUuid = uuid

    }

  }

  /**
   * Poslanie requestu pre konecne logovanie
   */
  public sendLogEnd(): void {

    // ak uz mame nastavene, tak uz viac nenastavujeme
    if (Object.keys(gameStats.getDisciplineDataToLog()).length > 0) return

    const timeToSend = playersManager.players[0].resultsArr?.[corePhasesManager.disciplineActualAttempt - 1].main || 0

    // zaznamename nejake info pre logy
    gameStats.setDisciplineDataToLog({
      time: Math.ceil(timeToSend * 100) / 100,
      dnf: playersManager.dnf,
      dsq: this.wasDsq,
      qualities: {
        start: Number(this.startQuality.toFixed(2)),
        run: disciplinePhasesManager.phaseRunning.getQualityRun(),
        lunge: Number(this.lungeQuality.toFixed(2))
      },
      playerPosition: playersManager.getPlayerActualPosition(),
      trainingTasks: modes.isTrainingMode() ?
        trainingManager.getTrainingTasks().map(task => task.value) :
        [],
      tutorialData: modes.isTutorial() ? this.getTutorialLogs() : []
    })

    console.log('LOG to send', gameStats.getDisciplineDataToLog())

  }

  /**
   * ziskame objekt tutorialovych logov
   *
   * @returns - objekt tutorialovych logov
   */
  private getTutorialLogs(): (number | boolean)[] {

    /**
     * completed - Ci hrac uspesne dokoncil vsetky ulohy [boolean]
     * exited - Ci bol ukonceny tutorial [boolean]
     */

    return [
      tutorialObjectives.falseStartCount,
      tutorialObjectives.checkIfObjectivePassedById(TutorialObjectiveIds.start),
      tutorialObjectives.checkIfObjectivePassedById(TutorialObjectiveIds.lunge),
      tutorialObjectives.checkIfObjectivePassedById(TutorialObjectiveIds.beatOpponent),
      tutorialObjectives.time,
      tutorialObjectives.isAllObjectivesPassed(),
      this.prematureEnded
    ]

  }

  /**
   * Vybratie dat a poslanie do funkcie z core-minigames
   */
  public sendSaveResult(): void {

    // ak uz bol result poslany, neposielame ho znova
    if (this.resultSent) return

    this.resultSent = true

    // TODO TEMP - zatial takto, ked bude hotovy tutorial, tak sa to bude posielat tam
    requestManager.sendTutorialRequest()
    if (modes.isTutorial()) return

    const opponentsResults = [] as OpponentResult[]
    playersManager.players.filter((playerInfo: PlayerInfo, index: number) => {

      return !playerInfo.playable && index <= gameConfig.numberOfOpponents

    }).forEach((playerInfo: PlayerInfo) => {

      const preResult = Number(((playerInfo.resultsArr?.[0].main || minigameConfig.dnfValue) * 100).toFixed(1))

      opponentsResults.push({
        uuid: playerInfo.uuid,
        time: Math.ceil(preResult) / 100
      })

    })

    // musime vybrat to lepsie umiestnenie a iba to budeme riesit (kedze su zoradeni, zoberieme prveho)
    const playerStanding = playersManager.getStandings().filter(standing => standing.uuid === player.uuid)
    const resultTime = Number(playerStanding[0]?.resultsFull?.[0]?.main) ?? 0

    const data: SaveResultsDataToSend = {
      time: resultTime,
      positions: playersManager.getPlayersPositions(),
      dnf: playersManager.dnf,
      dsq: this.wasDsq,
      opponentsResults: modes.isTrainingMode() ? [] : opponentsResults,
      perfectOrExcellentStart: this.perfectOrExcellentStart,
      perfectLunge: this.perfectLunge
    }

    if (modes.isTrainingMode()) {

      data.trainingResults = trainingManager.getTrainingTasks().map(task => task.value)

    }

    console.log('data to send', data)

    if (modes.isTournament()) {

      blurState().$patch({
        isActive: true,
        isTable: true
      })

    }
    requestManager.sendSaveResultsRequest(
      (dataCallback: TrainingDataFromResultsRequest | TournamentDataFromResultsRequest | unknown) => {

        console.log('saveRequest Successful')

        if (modes.isTrainingMode()) {

          trainingResultsState().$patch({
            data: dataCallback as TrainingDataFromResultsRequest,
            bestScore: trainingManager.bestScore,
            dataSet: true
          })

        }
        if (modes.isTournament()) {

          if (Number(import.meta.env.VITE_APP_LOCAL) === 1) {

            gsap.to({}, {
              onComplete: () => {

                disciplinePhasesManager.setOpponentsForFinishTable(dataCallback as TournamentDataFromResultsRequest)
                stateManager.afterAllRequestsDone()

              },
              duration: 5
            })

          } else {

            disciplinePhasesManager.setOpponentsForFinishTable(dataCallback as TournamentDataFromResultsRequest)

          }

        }

      },
      JSON.stringify(data)
    )

  }

  /**
   * Reset result
   */
  public reset(): void {

    this.resultSent = false
    this.winnerTime = minigameConfig.dnfValue
    this.winnerUuid = ''
    this.startQuality = 0
    this.lungeQuality = 0
    this.perfectOrExcellentStart = false
    this.perfectLunge = false

  }

}

export const endManager = new EndManager()
